import { Controller } from "@hotwired/stimulus"

// Roster drag-and-drop controller (Ticket 3).
//
// Usage:
//   <div data-controller="roster-drag"
//        data-roster-drag-url-value="/admin/planning_generations/123/drag_drops"
//        data-roster-drag-csrf-value="<%= form_authenticity_token %>">
//     <div data-roster-drag-target="cell"
//          data-slot-id="42"
//          data-date="2026-04-07"
//          data-weekend="false"
//          draggable="true">...</div>
//   </div>
//
// Mobile (coarse pointer) devices are skipped — drag is desktop-only.
export default class extends Controller {
  static targets = ["cell"]
  static values = {
    url: String,
    csrf: String,
    weekdayWeekendError: { type: String, default: "Werkdag en weekend kunnen niet gewisseld worden" },
    blockedError: { type: String, default: "Deze wijziging is geblokkeerd" },
    failedError: { type: String, default: "Wijziging mislukt" },
    networkError: { type: String, default: "Netwerkfout bij verplaatsen" },
    overrideReasonPrompt: { type: String, default: "Reden voor override (verplicht):" }
  }

  connect() {
    if (window.matchMedia("(pointer: coarse)").matches) return
    this.sourceEl = null
    this.boundDragStart = this.onDragStart.bind(this)
    this.boundDragOver  = this.onDragOver.bind(this)
    this.boundDragLeave = this.onDragLeave.bind(this)
    this.boundDrop      = this.onDrop.bind(this)
    this.boundDragEnd   = this.onDragEnd.bind(this)

    this.element.addEventListener("dragstart", this.boundDragStart)
    this.element.addEventListener("dragover",  this.boundDragOver)
    this.element.addEventListener("dragleave", this.boundDragLeave)
    this.element.addEventListener("drop",      this.boundDrop)
    this.element.addEventListener("dragend",   this.boundDragEnd)
  }

  disconnect() {
    if (!this.boundDragStart) return
    this.element.removeEventListener("dragstart", this.boundDragStart)
    this.element.removeEventListener("dragover",  this.boundDragOver)
    this.element.removeEventListener("dragleave", this.boundDragLeave)
    this.element.removeEventListener("drop",      this.boundDrop)
    this.element.removeEventListener("dragend",   this.boundDragEnd)
  }

  cellFrom(event) {
    return event.target.closest('[data-roster-drag-target="cell"]')
  }

  onDragStart(event) {
    const cell = this.cellFrom(event)
    if (!cell || !cell.dataset.slotId) { event.preventDefault(); return }
    this.sourceEl = cell
    cell.classList.add("roster-drag-source")
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", cell.dataset.slotId)
  }

  onDragOver(event) {
    const cell = this.cellFrom(event)
    if (!cell || !this.sourceEl || cell === this.sourceEl) return
    if (cell.dataset.weekend !== this.sourceEl.dataset.weekend) return
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    cell.classList.add("roster-drag-over")
  }

  onDragLeave(event) {
    const cell = this.cellFrom(event)
    if (cell) cell.classList.remove("roster-drag-over")
  }

  onDragEnd() {
    if (this.sourceEl) this.sourceEl.classList.remove("roster-drag-source")
    this.element.querySelectorAll(".roster-drag-over").forEach(el => el.classList.remove("roster-drag-over"))
    this.sourceEl = null
  }

  async onDrop(event) {
    event.preventDefault()
    const target = this.cellFrom(event)
    const source = this.sourceEl
    if (!target || !source || target === source) return this.onDragEnd()
    target.classList.remove("roster-drag-over")

    if (target.dataset.weekend !== source.dataset.weekend) {
      this.snapBack(source, this.weekdayWeekendErrorValue)
      return this.onDragEnd()
    }

    try {
      const body = { source_slot_id: source.dataset.slotId, target_slot_id: target.dataset.slotId }
      let response = await this.post(body)

      if (response.status === 422) {
        const data = await response.json().catch(() => ({}))
        if (!data.override_allowed) {
          this.snapBack(source, data.label || data.error || this.failedErrorValue)
          return
        }
        const reason = window.prompt(`${data.error || data.label}\n\n${this.overrideReasonPromptValue}`)
        if (!reason || !reason.trim()) {
          this.snapBack(source, data.label || data.error || this.failedErrorValue)
          return
        }
        response = await this.post({ ...body, override_acknowledged: true, override_reason: reason.trim() })
      }

      if (response.ok) {
        const ct = response.headers.get("Content-Type") || ""
        if (ct.includes("turbo-stream")) {
          Turbo.renderStreamMessage(await response.text())
        } else {
          window.location.reload()
        }
      } else if (response.status === 403) {
        const data = await response.json().catch(() => ({}))
        this.shake(source)
        this.toast(data.label || data.error || this.blockedErrorValue)
      } else {
        const data = await response.json().catch(() => ({}))
        this.snapBack(source, data.label || data.error || this.failedErrorValue)
      }
    } catch (_err) {
      this.snapBack(source, this.networkErrorValue)
    } finally {
      this.onDragEnd()
    }
  }

  post(body) {
    return fetch(this.urlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "text/vnd.turbo-stream.html, application/json",
        "X-CSRF-Token": this.csrfValue
      },
      body: JSON.stringify(body)
    })
  }

  snapBack(el, message) {
    el.classList.add("roster-drag-snapback")
    setTimeout(() => el.classList.remove("roster-drag-snapback"), 400)
    this.toast(message)
  }

  shake(el) {
    el.classList.add("roster-drag-shake")
    setTimeout(() => el.classList.remove("roster-drag-shake"), 500)
  }

  toast(message) {
    const container = document.getElementById("flash_messages_container")
    if (!container) return console.warn("[roster-drag]", message)
    const flash = document.createElement("div")
    flash.className = "flash-message bg-red-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center transition-all duration-300"
    flash.dataset.controller = "toast"
    flash.dataset.toastDurationValue = "5000"
    flash.setAttribute("role", "alert")
    const text = document.createElement("p")
    text.textContent = message
    flash.append(text)
    container.append(flash)
  }
}
